import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Layout from '../../layouts/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import TimelineMetric from '../../components/ui/TimelineMetric';
import PropertyCard from '../../components/PropertyCard';
import {
  Users, Calendar, TrendingUp, Clock, CheckCircle, Home, Eye,
  Phone, Mail, Euro, MapPin, ArrowRight, Building2, MessageSquare
} from 'lucide-react';
import dashboardIcon from '../../assets/img3d/dashboard.png';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfessionalDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dashboardStats, setDashboardStats] = useState({
    pending_leads: 0,
    accepted_leads: 0,
    week_visits: 0,
    month_conversions: 0,
    total_leads: 0
  });
  const [propertyStats, setPropertyStats] = useState({
    total_properties: 0,
    published: 0,
    draft: 0,
    total_views: 0,
    total_contacts: 0
  });
  const [pendingLeads, setPendingLeads] = useState([]);
  const [upcomingVisits, setUpcomingVisits] = useState([]);
  const [recentProperties, setRecentProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchDashboardStats(),
        fetchPropertyStats(),
        fetchPendingLeads(),
        fetchUpcomingVisits(),
        fetchRecentProperties()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/professional/dashboard/stats`);
      const data = response.data;
      // Map camelCase to snake_case for consistency
      setDashboardStats({
        pending_leads: data.pendingLeads || data.pending_leads || 0,
        accepted_leads: data.acceptedLeads || data.accepted_leads || 0,
        week_visits: data.weekVisits || data.week_visits || 0,
        month_conversions: data.monthConversions || data.month_conversions || 0,
        total_leads: data.totalLeads || data.total_leads || 0
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const fetchPropertyStats = async () => {
    try {
      const response = await axios.get(`${API}/professional/stats`);
      const data = response.data;
      // Map camelCase to snake_case for consistency
      setPropertyStats({
        total_properties: data.totalProperties || data.total_properties || 0,
        published: data.published || 0,
        draft: data.draft || 0,
        total_views: data.totalViews || data.total_views || 0,
        total_contacts: data.totalContacts || data.total_contacts || 0
      });
    } catch (error) {
      console.error('Error fetching property stats:', error);
    }
  };

  const fetchPendingLeads = async () => {
    try {
      const response = await axios.get(`${API}/professional/leads?professionalStatus=PENDING&limit=3`);
      const leadsData = response.data;
      setPendingLeads(Array.isArray(leadsData) ? leadsData : (leadsData?.items || []));
    } catch (error) {
      console.error('Error fetching leads:', error);
      setPendingLeads([]);
    }
  };

  const fetchUpcomingVisits = async () => {
    try {
      const response = await axios.get(`${API}/professional/visits?upcomingOnly=true&limit=5`);
      const visitsData = response.data;
      const visitsArray = Array.isArray(visitsData) ? visitsData : (visitsData?.items || []);
      const filtered = visitsArray.filter((v) => v.visitDate || v.visit_date);
      setUpcomingVisits(filtered);
    } catch (error) {
      console.error('Error fetching visits:', error);
      setUpcomingVisits([]);
    }
  };

  const fetchRecentProperties = async () => {
    try {
      const response = await axios.get(`${API}/professional/properties?limit=3`);
      const data = response.data;
      const propertiesArray = Array.isArray(data) ? data : (data?.items || []);
      setRecentProperties(propertiesArray);
    } catch (error) {
      console.error('Error fetching properties:', error);
      setRecentProperties([]);
    }
  };

  // TODO: Réactiver quand la logique de score sera implémentée
  // const getScoreBadge = (score) => {
  //   if (score >= 75) {
  //     return <Badge className="bg-red-100 text-red-800">🔥 {score}</Badge>;
  //   } else if (score >= 50) {
  //     return <Badge className="bg-yellow-100 text-yellow-800">⚡ {score}</Badge>;
  //   } else {
  //     return <Badge className="bg-gray-100 text-gray-800">❄️ {score}</Badge>;
  //   }
  // };

  // const getScoreLabel = (score) => {
  //   if (score >= 75) return 'Très chaud';
  //   if (score >= 50) return 'Chaud';
  //   return 'Froid';
  // };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date non définie';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Date invalide';
      return date.toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Date invalide';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Date non définie';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Date invalide';
      return date.toLocaleString('fr-FR', { 
        weekday: 'short',
        day: 'numeric', 
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Date invalide';
    }
  };

  const formatRelativeDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const now = new Date();
      const diffTime = date - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return "Aujourd'hui";
      if (diffDays === 1) return 'Demain';
      if (diffDays === -1) return 'Hier';
      if (diffDays > 0 && diffDays <= 7) return `Dans ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
      if (diffDays < 0 && diffDays >= -7) return `Il y a ${Math.abs(diffDays)} jour${Math.abs(diffDays) > 1 ? 's' : ''}`;
      
      return formatDate(dateString);
    } catch (error) {
      return '';
    }
  };

  return (
    <Layout
      headerConfig={{
        icon: dashboardIcon,
        iconColor: 'bg-white',
        title: 'Dashboard Professionnel',
        subtitle: `Vue d'ensemble de votre activité`,
        primaryAction: undefined,
        secondaryAction: undefined,
        tertiaryAction: undefined,
        onPrimaryAction: undefined,
        onSecondaryAction: undefined,
        onTertiaryAction: undefined
      }}
    >
      {/* KPIs Principaux - 2 lignes */}
      <div className="mb-8 space-y-4">
        {/* Ligne 1: Leads & Visites */}
        <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
          <div className="flex flex-col md:flex-row w-full">
            <div className="flex-1">
              <TimelineMetric
                icon={Clock}
                value={dashboardStats.pending_leads}
                label="Leads en Attente"
                type="arrears"
                onClick={dashboardStats.pending_leads > 0 ? () => navigate('/professional/leads?filter=pending') : undefined}
              />
            </div>
            <div className="flex-1">
              <TimelineMetric
                icon={Eye}
                value={propertyStats.total_views}
                label="Total Vues"
                type="occupancy"
              />
            </div>
            <div className="flex-1">
              <TimelineMetric
                icon={Home}
                value={propertyStats.total_properties}
                label="Total Annonces"
                type="properties"
                onClick={() => navigate('/dashboard')}
              />
            </div>
            <div className="flex-1">
              <TimelineMetric
                icon={Calendar}
                value={dashboardStats.week_visits}
                label="Visites Semaine"
                type="occupancy"
                onClick={dashboardStats.week_visits > 0 ? () => navigate('/professional/visits') : undefined}
              />
            </div>
            <div className="flex-1">
              <TimelineMetric
                icon={TrendingUp}
                value={dashboardStats.month_conversions}
                label="Conversions Mois"
                type="owners"
                noBorder={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Grille de contenu: 3 colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne 1: Leads Urgents */}
        <Card className="lg:col-span-1">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                Leads Urgents
              </h2>
              {pendingLeads.length > 0 && (
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/professional/leads')}
                >
                  Voir tout
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF9F68]"></div>
              </div>
            ) : pendingLeads.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Aucun lead en attente</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/professional/leads/${lead.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-sm font-semibold text-gray-900">
                            {lead.seekerName || lead.seeker_name}
                          </h3>
                          {/* {getScoreBadge(lead.score)} */}
                        </div>
                        <div className="mb-2">
                          {/* TODO: Réactiver quand la logique de score sera implémentée */}
                          {/* <p className="text-xs text-gray-500 mb-1">
                            Score: <span className="font-medium text-gray-700">{lead.score}/100</span> - {getScoreLabel(lead.score)}
                          </p> */}
                          <p className="text-xs text-gray-600 line-clamp-1">
                            {lead.seekerEmail || lead.seeker_email}
                          </p>
                        </div>
                        {/* {lead.budgetMax || lead.budget_max ? (
                          <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                            <Euro className="h-3 w-3" />
                            Budget: {(lead.budgetMax || lead.budget_max).toLocaleString('fr-FR')}€
                          </div>
                        ) : null} */}
                        {lead.seekerMessage || lead.seeker_message ? (
                          <p className="text-xs text-gray-600 line-clamp-2 mt-2 italic">
                            "{lead.seekerMessage || lead.seeker_message}"
                          </p>
                        ) : null}
                        <div className="flex items-center gap-1 text-xs text-gray-400 mt-2">
                          <Clock className="h-3 w-3" />
                          Reçu le {formatDate(lead.createdAt || lead.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Colonne 2: Visites à Venir */}
        <Card className="lg:col-span-1">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                Visites à Venir
              </h2>
              {upcomingVisits.length > 0 && (
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/professional/visits')}
                >
                  Voir tout
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF9F68]"></div>
              </div>
            ) : upcomingVisits.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Aucune visite planifiée</p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingVisits.map((visit) => {
                  const visitDate = visit.visitDate || visit.visit_date;
                  const seekerName = visit.seekerName || visit.seeker_name;
                  const propertyTitle = visit.property?.title || visit.property_title;
                  const propertyLocation = visit.property?.location || visit.property_address;
                  
                  return (
                    <div
                      key={visit.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/professional/leads/${visit.id}`)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="h-4 w-4 text-[#FF9F68]" />
                            <div>
                              <span className="text-sm font-semibold text-gray-900">
                                {formatRelativeDate(visitDate)}
                              </span>
                              {visitDate && (
                                <span className="text-xs text-gray-500 ml-2">
                                  {new Date(visitDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </div>
                          </div>
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            {seekerName}
                          </p>
                          {propertyTitle && (
                            <p className="text-xs text-gray-600 line-clamp-1 mb-1">
                              {propertyTitle}
                            </p>
                          )}
                          {propertyLocation && (
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <MapPin className="h-3 w-3" />
                              {propertyLocation}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Colonne 3: Mes Annonces Récentes */}
        <Card className="lg:col-span-1">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                Mes Annonces
              </h2>
              {recentProperties.length > 0 && (
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/dashboard')}
                >
                  Voir tout
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF9F68]"></div>
              </div>
            ) : recentProperties.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm mb-3">Aucune annonce</p>
                <Button
                  size="sm"
                  onClick={() => navigate('/add-property')}
                  className="bg-[#FF9F68] hover:bg-[#E67E4F] text-white"
                >
                  Créer une annonce
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {recentProperties.map((property) => (
                  <div
                    key={property.id}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/properties/${property.id}`)}
                  >
                    {property.images && property.images.length > 0 && (
                      <img
                        src={property.images[0]}
                        alt={property.title}
                        className="w-full h-24 object-cover"
                      />
                    )}
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-gray-900 line-clamp-1 mb-1">
                        {property.title}
                      </h3>
                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <span>{property.location}</span>
                        {(property.viewsCount !== undefined || property.views_count !== undefined) && (
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {property.viewsCount || property.views_count || 0}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ProfessionalDashboard;
